import { useState } from "react";

import type { IconName } from "@similarweb/icons";

import type { ICategory, ICustomCategory } from "app/common/services/categoryService.types";

import { useCategoriesComparison } from "../../categories-context/CategoriesContext";
import { useIndustryCategoryActions } from "../hooks/useIndustryCategoryActions";
import { QueryBarContainer } from "../styles";
import type { ICategoriesData, ICategoryModalProps } from "../types";

import { CategoryQueryBarItem } from "./CategoryQueryBarItem";
import { CompareButton } from "./CompareButton";
import { SingleCategoryQueryBarItem } from "./SingleCategoryQueryBarItem";

interface IIndustryAnalysisQueryBarContainerProps {
    categoriesData: ICategoriesData;
    selectedCategory: ICategory | ICustomCategory | null;
    isSearchOpen: boolean;
    showOnlyCategories?: boolean;
    showOnlyCustom?: boolean;
    placeholder?: string;
    categoryModalProps?: ICategoryModalProps;
    isPartnerPage?: boolean;
    customResolveItemIcon?: (item: ICategory, isChild: boolean) => IconName;
    onItemClick: (item: ICategory) => void;
    onQueryBarItemClick: () => void;
    onSearchClose: () => void;
}

export const IndustryAnalysisQueryBarContainer = ({
    categoriesData,
    selectedCategory,
    isSearchOpen,
    showOnlyCategories,
    showOnlyCustom,
    placeholder,
    categoryModalProps,
    isPartnerPage,
    customResolveItemIcon,
    onItemClick,
    onQueryBarItemClick,
    onSearchClose,
}: IIndustryAnalysisQueryBarContainerProps) => {
    const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
    const { categories, isComparisonMode, isMaxCategoriesReached, hasSelectedCategoryWithSegments } =
        useCategoriesComparison();

    const { handleCategoryReplace, handleCompareCategorySelect, handleCategoryRemove } = useIndustryCategoryActions(
        categoriesData,
        selectedCategory,
    );

    const allSelectedCategories = categories.map((categoryComparison) => categoryComparison.category);

    const handleCategoryClick = (categoryId: string) => {
        setActiveCategoryId(categoryId);
    };

    const handleSearchClose = () => {
        setActiveCategoryId(null);
    };

    return (
        <QueryBarContainer>
            {categories.length > 0 ? (
                categories.map((category) => (
                    <CategoryQueryBarItem
                        key={category.category.forUrl || category.category.categoryId || category.category.id}
                        category={category}
                        activeCategoryId={activeCategoryId}
                        showOnlyCategories={showOnlyCategories}
                        showOnlyCustom={showOnlyCustom}
                        placeholder={placeholder}
                        categoryModalProps={categoryModalProps}
                        isPartnerPage={isPartnerPage}
                        isCompare={isComparisonMode}
                        customResolveItemIcon={customResolveItemIcon}
                        onCategoryClick={handleCategoryClick}
                        onCategoryRemove={handleCategoryRemove}
                        onCategoryReplace={handleCategoryReplace}
                        onSearchClose={handleSearchClose}
                        disabledCategories={isComparisonMode ? allSelectedCategories : []}
                        hasSelectedCategoryWithSegments={isComparisonMode ? hasSelectedCategoryWithSegments : undefined}
                    />
                ))
            ) : (
                <SingleCategoryQueryBarItem
                    categoriesData={categoriesData}
                    selectedCategory={selectedCategory}
                    isSearchOpen={isSearchOpen}
                    showOnlyCategories={showOnlyCategories}
                    showOnlyCustom={showOnlyCustom}
                    placeholder={placeholder}
                    categoryModalProps={categoryModalProps}
                    isPartnerPage={isPartnerPage}
                    customResolveItemIcon={customResolveItemIcon}
                    onItemClick={onItemClick}
                    onQueryBarItemClick={onQueryBarItemClick}
                    onSearchClose={onSearchClose}
                />
            )}

            {!isMaxCategoriesReached ? (
                <CompareButton
                    showOnlyCategories={showOnlyCategories}
                    showOnlyCustom={showOnlyCustom}
                    placeholder={placeholder}
                    categoryModalProps={categoryModalProps}
                    isPartnerPage={isPartnerPage}
                    customResolveItemIcon={customResolveItemIcon}
                    selectedCategory={selectedCategory}
                    onCompareCategorySelect={handleCompareCategorySelect}
                    disabledCategories={allSelectedCategories}
                    hasSelectedCategoryWithSegments={hasSelectedCategoryWithSegments}
                />
            ) : null}
        </QueryBarContainer>
    );
};
